import {
  AngularNodeAppEngine,
  createNodeRequestHandler,
  isMainModule,
  writeResponseToNodeResponse,
} from '@angular/ssr/node';
import express from 'express';
import { join } from 'node:path';

const browserDistFolder = join(import.meta.dirname, '../browser');

// Behind a reverse proxy (nginx, etc.) the "Host" header seen by Node is the
// internal one (e.g. "localhost:8001"), not the public domain the browser
// requested. Angular SSR validates the request's host/origin for SSRF
// protection, so we must tell it which forwarded headers to trust and which
// public hostnames are legitimate. Both can be overridden at deploy time via
// the NG_TRUST_PROXY_HEADERS / NG_ALLOWED_HOSTS env vars (comma-separated)
// without touching code — see https://angular.dev/best-practices/security#preventing-server-side-request-forgery-ssr
const app = express();
const angularApp = new AngularNodeAppEngine({
  trustProxyHeaders: ['x-forwarded-host', 'x-forwarded-proto', 'x-forwarded-port'],
  allowedHosts: [
    'forevermemoriesdev.alliancetek.net',
    'forevermemoriesqa.alliancetek.net',
    'forevermemoriesstage.alliancetek.net',
    'forevermemories.alliancetek.net',
    'localhost',
  ],
});

// Trust the reverse proxy so req.protocol/req.ip reflect the original client
// request instead of the internal proxy connection.
app.set('trust proxy', true);

// Allow OAuth popups (Google, Facebook, Apple) to communicate with the opener.
// Also explicitly grant camera & microphone permissions so getUserMedia() works
// on the deployed domain — without this header some hosts/proxies inject a
// restrictive default (camera=(), microphone=()) that blocks recording.
app.use((_req, res, next) => {
  res.setHeader('Cross-Origin-Opener-Policy', 'same-origin-allow-popups');
  res.setHeader('Cross-Origin-Embedder-Policy', 'unsafe-none');
  res.setHeader('Permissions-Policy', 'camera=(self), microphone=(self)');
  next();
});

/**
 * Example Express Rest API endpoints can be defined here.
 * Uncomment and define endpoints as necessary.
 *
 * Example:
 * ```ts
 * app.get('/api/{*splat}', (req, res) => {
 *   // Handle API request
 * });
 * ```
 */

/**
 * Serve static files from /browser
 */
app.use(
  express.static(browserDistFolder, {
    maxAge: '1y',
    index: false,
    redirect: false,
  }),
);

/**
 * Handle all other requests by rendering the Angular application.
 */
app.use((req, res, next) => {
  angularApp
    .handle(req)
    .then((response) =>
      response ? writeResponseToNodeResponse(response, res) : next(),
    )
    .catch(next);
});

/**
 * Start the server if this module is the main entry point, or it is ran via PM2.
 * The server listens on the port defined by the `PORT` environment variable,
 * falling back to the value configured in the Angular environment file.
 */
if (isMainModule(import.meta.url) || process.env['pm_id']) {
  const port = process.env['PORT'] ?? 8001;
  app.listen(port, () => {
    console.log(`Node Express server listening on http://localhost:${port}`);
  });
}

/**
 * Request handler used by the Angular CLI (for dev-server and during build) or Firebase Cloud Functions.
 */
export const reqHandler = createNodeRequestHandler(app);
